/** Mapea lo que traen el backend y los escaneos a uno de los valores del desplegable (ver
 * `genderOptions`). Las claves se comparan en minúscula. Todo lo que no esté acá —"n/a", con el que
 * se dieron de alta los usuarios de los que no se pudo definir el género, el viejo "other", o
 * cualquier valor desconocido— cae en vacío: el desplegable queda sin elegir y, como el campo es
 * obligatorio, el guardia tiene que cargarlo. */
const SEX_NORMALIZATION_MAP: Record<string, string> = {
  male: "male",
  m: "male",
  masculino: "male",
  female: "female",
  f: "female",
  femenino: "female",
  nonbinary: "nonBinary",
  "no binario": "nonBinary",
  "1": "male",
  "2": "female",
};

const isValidDate = (value: Date) => !Number.isNaN(value.getTime());

const parseDateFromString = (value: string): Date | null => {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  const localDateMatch = trimmed.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/);
  if (localDateMatch) {
    const [, dayString, monthString, yearString] = localDateMatch;
    const year =
      yearString.length === 2
        ? Number(`19${yearString}`)
        : yearString.length === 3
        ? Number(`2${yearString}`)
        : Number(yearString);
    const month = Number(monthString) - 1;
    const day = Number(dayString);

    const parsedDate = new Date(year, month, day);
    if (
      parsedDate.getFullYear() !== year ||
      parsedDate.getMonth() !== month ||
      parsedDate.getDate() !== day
    ) {
      return null;
    }

    return parsedDate;
  }

  const directDate = new Date(trimmed);
  if (!isValidDate(directDate)) {
    return null;
  }

  const year = directDate.getFullYear();
  const month = directDate.getMonth();
  const day = directDate.getDate();
  const parsedDate = new Date(year, month, day);
  return parsedDate;
};

export const parseBirthDateValue = (value: unknown): Date | null => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return isValidDate(value) ? value : null;
  }

  if (typeof value === "number") {
    const timestamp = value < 1_000_000_000_000 ? value * 1000 : value;
    const parsedDate = new Date(timestamp);
    return isValidDate(parsedDate) ? parsedDate : null;
  }

  if (typeof value === "string") {
    return parseDateFromString(value);
  }

  return null;
};

export const normalizeSexValue = (value: unknown): string => {
  if (!value || typeof value !== "string") {
    return "";
  }

  const normalized = value.trim().toLowerCase();
  return SEX_NORMALIZATION_MAP[normalized] || "";
};

export const resolveUserBirthdate = (userData: any): Date | null => {
  if (!userData) {
    return null;
  }

  const candidates = [userData.birthdate];

  for (const candidate of candidates) {
    const parsedDate = parseBirthDateValue(candidate);
    if (parsedDate) {
      return parsedDate;
    }
  }

  return null;
};

export const resolveUserSex = (userData: any): string => {
  if (!userData) {
    return "";
  }

  return normalizeSexValue(userData.sex);
};

export const isUnderAge = (birthDate: Date | null, legalAge = 18): boolean => {
  if (!birthDate) {
    return false;
  }

  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const hasNotHadBirthdayYet =
    today.getMonth() < birthDate.getMonth() ||
    (today.getMonth() === birthDate.getMonth() && today.getDate() < birthDate.getDate());

  if (hasNotHadBirthdayYet) {
    age -= 1;
  }

  return age < legalAge;
};

export const areSameCalendarDate = (a: Date | null, b: Date | null): boolean => {
  if (!a && !b) {
    return true;
  }

  if (!a || !b) {
    return false;
  }

  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
};
